import axios, { isAxiosError, type InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../stores/auth';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';
let refreshPromise: Promise<{ accessToken: string; refreshToken: string }> | null = null;
let hasShownSessionWarning = false;

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

async function refreshAuthTokens(): Promise<{ accessToken: string; refreshToken: string }> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (!refreshToken) {
      throw new Error('No refresh token');
    }

    const { data } = await axios.post(
      `${API_URL}/auth/refresh`,
      {},
      {
        headers: { Authorization: `Bearer ${refreshToken}` },
      },
    );

    const { accessToken, refreshToken: newRefresh } = data.data.tokens;
    useAuthStore.getState().setTokens(accessToken, newRefresh);
    return { accessToken, refreshToken: newRefresh };
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = (error.config ?? {}) as RetryableRequestConfig;
    const requestUrl = originalRequest?.url ?? '';
    const isRefreshRequest = typeof requestUrl === 'string' && requestUrl.includes('/auth/refresh');

    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest) {
      originalRequest._retry = true;
      try {
        const { accessToken } = await refreshAuthTokens();
        if (originalRequest.headers && typeof originalRequest.headers.set === 'function') {
          originalRequest.headers.set('Authorization', `Bearer ${accessToken}`);
        } else {
          originalRequest.headers = {
            ...(originalRequest.headers ?? {}),
            Authorization: `Bearer ${accessToken}`,
          } as RetryableRequestConfig['headers'];
        }
        return api(originalRequest);
      } catch (refreshError) {
        const refreshStatus = isAxiosError(refreshError)
          ? refreshError.response?.status
          : undefined;
        if (refreshStatus === 401 || refreshStatus === 403) {
          useAuthStore.getState().logout();
          if (window.location.pathname !== '/login') {
            const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
          }
        } else if (!hasShownSessionWarning) {
          hasShownSessionWarning = true;
          toast.error('Session refresh failed temporarily. Please retry in a moment.', {
            id: 'admin-session-warning',
          });
          window.setTimeout(() => {
            hasShownSessionWarning = false;
          }, 5000);
        }
      }
    }
    return Promise.reject(error);
  },
);

export default api;
